import { cn, ResizablePanelGroup, ResizablePanel, ResizableHandle, type ImperativePanelGroupHandle, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { useState, useCallback, useRef, useEffect, useMemo, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { WorkspaceTabsView, WorkspaceTabsViewRef } from './WorkspaceTabsView'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import { debounce } from 'lodash'
import DOMPurify from 'dompurify'
import { useSetAtom, useAtomValue, useStore } from 'jotai'
import { lastSaveWorkDateAtom, isSavingWorkAtom, currentSolutionAtom } from '@/store/saveWorkAtoms'
import { workspaceTabsHasTwoViewsAtom, workspaceSolutionHandlersAtom } from '@/store/workspaceAtoms'
import { useEventEmitter, useEventListener } from '@/modules/EventBusModule'
import { getChartContainerHtml, getSolutionBlockWithDeleteButtonHtml } from '@/components/WysiwygEditor/wysiwygUtils'
import { trackWorkspace } from '@/modules/workspaceAnalytics'
import { AnalyticsTimer } from '@/modules/analyticsTimer'
import { useAvailableWorkspaceTabs } from '@/hooks/useAvailableWorkspaceTabs'

const LazyPythonNotebook = lazy(() =>
    import('../PythonNotebook/PythonNotebook').then(m => ({ default: m.PythonNotebook }))
)

type ViewTabState = ChallengeService.WorkspaceTab

type ViewState = {
    tabs: ViewTabState[]
    activeTabIndex: number
}

const hasSolutionTabInViews = (views: ViewState[]): boolean =>
    views.some((v) => v.tabs.some((t) => t.type === ChallengeService.WorkspaceTabTypeEnum.Solution))

type TabViews = NonNullable<ChallengeService.WorkState['tabViews']>

/** Drop any tabs the user can't use (e.g. gated by badge, or removed from the codebase). */
const stripUnavailableTabs = (
    views: ViewState[],
    allowed: Set<ChallengeService.WorkspaceTabTypeEnum>
): ViewState[] => {
    const filtered = views
        .map((view) => {
            const tabs = view.tabs.filter((t) => allowed.has(t.type))
            if (tabs.length === view.tabs.length) return view
            const activeTabIndex = tabs.length === 0 ? 0 : Math.min(view.activeTabIndex, tabs.length - 1)
            return { tabs, activeTabIndex }
        })
        .filter((view) => view.tabs.length > 0)
    return filtered.length > 0 ? filtered : views
}

// Convert contract tabViews to component view state
const contractTabViewsToViews = (tabViews?: ChallengeService.WorkState['tabViews'], defaultQuery?: string): ViewState[] => {
    if (!tabViews || tabViews.length === 0) {
        // Default state if no tabs saved
        return [
            {
                tabs: [
                    { type: ChallengeService.WorkspaceTabTypeEnum.Assignment },
                    { type: ChallengeService.WorkspaceTabTypeEnum.Sql, data: defaultQuery },
                    { type: ChallengeService.WorkspaceTabTypeEnum.Solution },
                ],
                activeTabIndex: 0,
            }
        ]
    }
    return tabViews.map((tabView) => {
        const tabs = tabView.tabs.map((tab) => ({
            type: tab.type,
            data: tab.data,
        }))
        // Persisted activeTab may be out of range (e.g. after tabs were removed); clamp to valid index to avoid downstream indexing errors.
        const activeTab = tabView.activeTab
        const activeTabIndex =
            tabs.length === 0 ? 0 : Math.min(Math.max(0, activeTab), tabs.length - 1)
        return { tabs, activeTabIndex }
    })
}

// Convert component view state to contract tabViews
const viewsToContractTabViews = (views: ViewState[], widths?: number[]): TabViews => {
    return views.map((view, index): ChallengeService.WorkspaceTabView => ({
        activeTab: view.activeTabIndex,
        tabs: view.tabs.map(
            (tab): ChallengeService.WorkspaceTab => ({
                type: tab.type,
                data: tab.data,
            })
        ),
        width: widths?.[index],
    }))
}

export interface WorkspaceTabsProps {
    challenge: ChallengeService.ChallengeInfo['challenge']
    challengeId: string
    defaultQuery?: string
    className?: string
}

const MAX_VIEWS = 2

export const WorkspaceTabs = ({
    challenge,
    challengeId,
    defaultQuery = '',
    className,
}: WorkspaceTabsProps) => {
    const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
    const setIsSaving = useSetAtom(isSavingWorkAtom)
    const setHasTwoViews = useSetAtom(workspaceTabsHasTwoViewsAtom)
    const availableTabs = useAvailableWorkspaceTabs()
    const pythonAllowed = availableTabs.has(ChallengeService.WorkspaceTabTypeEnum.Python)

    // Initialize from contract data
    const { initialViews, initialWidths } = useMemo(() => {
        const tabViews = challenge.workState?.tabViews
        const rawViews = contractTabViewsToViews(tabViews, defaultQuery)
        const views = stripUnavailableTabs(rawViews, availableTabs)
        const widths = tabViews?.map((tv) => (typeof tv.width === 'number' ? tv.width : 50)) ?? []

        return { initialViews: views, initialWidths: widths }
    }, [challenge.workState, defaultQuery, availableTabs])

    const [views, setViews] = useState<ViewState[]>(initialViews)
    const [panelWidths, setPanelWidths] = useState<number[]>(initialWidths)
    const loadTimerRef = useRef(new AnalyticsTimer())
    const loadTrackedRef = useRef(false)

    useEffect(() => {
        if (!loadTrackedRef.current && views.length > 0 && views[0].tabs.length > 0) {
            loadTrackedRef.current = true
            requestAnimationFrame(() => {
                loadTimerRef.current.complete()
                trackWorkspace('workspace:load', {
                    challengeId,
                    tabCount: views.reduce((sum, v) => sum + v.tabs.length, 0),
                    viewCount: views.length,
                    durationMs: loadTimerRef.current.totalMs,
                })
            })
        }
    }, [views, challengeId])

    const viewsRef = useRef<ViewState[]>(initialViews)
    const secondViewRef = useRef<WorkspaceTabsViewRef>(null)
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null)
    const [draggingFromView, setDraggingFromView] = useState<number | null>(null)
    const [draggingTabType, setDraggingTabType] = useState<ChallengeService.WorkspaceTabTypeEnum | null>(null)
    const viewContainerRefs = useRef<(HTMLDivElement | null)[]>([null, null])

    /** Key for WorkspaceTabsView: changes only when view structure changes (tabs added/removed/reordered). Typing changes tab.data but not structure, so no remount. */
    const viewKeys = useMemo(
        () =>
            views.map((v, i) => `${i}-${v.tabs.length}-${v.tabs.map((t) => t.type).join(',')}`),
        [views]
    )

    const openTabTypes = useMemo(() => {
        const types = new Set<ChallengeService.WorkspaceTabTypeEnum>()
        for (const view of views) {
            for (const tab of view.tabs) {
                types.add(tab.type)
            }
        }
        return types
    }, [views])

    const pythonHostRef = useRef<HTMLDivElement | null>(null)
    if (!pythonHostRef.current) {
        pythonHostRef.current = document.createElement('div')
        pythonHostRef.current.className = 'h-full w-full'
    }

    const [pythonPortalTarget, setPythonPortalTarget] = useState<HTMLElement | null>(null)

    const hasPythonTab = openTabTypes.has(ChallengeService.WorkspaceTabTypeEnum.Python)

    // Once the Python notebook mounts, keep it alive even after the tab is closed.
    // This preserves kernel state, cell outputs, and WebSocket connections.
    const [pythonEverMounted, setPythonEverMounted] = useState(false)
    useEffect(() => {
        if (hasPythonTab) setPythonEverMounted(true)
    }, [hasPythonTab])

    const handlePortalTargetChange = useCallback(
        (_tabType: ChallengeService.WorkspaceTabTypeEnum, el: HTMLElement | null) => {
            setPythonPortalTarget(el)
        },
        []
    )

    useEffect(() => {
        const host = pythonHostRef.current
        if (!host) return
        if (pythonPortalTarget) {
            pythonPortalTarget.appendChild(host)
        } else if (host.parentElement) {
            host.parentElement.removeChild(host)
        }
        return () => {
            if (host.parentElement) {
                host.parentElement.removeChild(host)
            }
        }
    }, [pythonPortalTarget])

    const solutionHandlers = useAtomValue(workspaceSolutionHandlersAtom)
    const store = useStore()
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const solutionRef = useRef<string>('')
    useEffect(() => {
        solutionRef.current = store.get(currentSolutionAtom)
        const unsub = store.sub(currentSolutionAtom, () => {
            solutionRef.current = store.get(currentSolutionAtom)
        })
        return unsub
    }, [store])

    // When no Solution tab is open, append insert/chart to workState so it persists
    useEventListener(
        'solution:insertHtml',
        ({ value: html, deleteButton }) => {
            if (hasSolutionTabInViews(views)) return
            const sanitized = DOMPurify.sanitize(html)
            const toAppend = deleteButton
                ? getSolutionBlockWithDeleteButtonHtml(sanitized)
                : sanitized + '<div>&nbsp;</div>'
            setCurrentSolution((prev) => {
                const next = prev + toAppend
                solutionHandlers?.handleSolutionChange(next)
                return next
            })
        },
        [views, solutionHandlers, setCurrentSolution]
    )
    useEventListener(
        'chart:added',
        ({ chart }) => {
            if (!chart?.id) return
            const newSolution = solutionRef.current + getChartContainerHtml(chart)
            if (!hasSolutionTabInViews(views)) {
                // No Solution tab open — append chart HTML to workState so it persists
                setCurrentSolution(newSolution)
            }
            // Single save + toast for chart addition regardless of Solution tab count.
            // When Solution tabs exist, they handle DOM insertion themselves via the same event.
            solutionHandlers?.handleChartAdded(newSolution, chart)
        },
        [views, solutionHandlers, setCurrentSolution]
    )

    // Set initial two views state
    useEffect(() => {
        setHasTwoViews(initialViews.length === 2)
    }, []) // Only run on mount

    // Update views when challenge data changes
    useEffect(() => {
        const tabViews = challenge.workState?.tabViews
        const rawViews = contractTabViewsToViews(tabViews, defaultQuery)
        const newViews = stripUnavailableTabs(rawViews, availableTabs)
        const newWidths = tabViews?.map((tv) => (typeof tv.width === 'number' ? tv.width : 50)) ?? []
        setViews(newViews)
        viewsRef.current = newViews
        setPanelWidths(newWidths)
        setHasTwoViews(newViews.length === 2)
    }, [challenge.workState, setHasTwoViews, defaultQuery, availableTabs])

    // Debounced save function
    const saveWorkspaceTabsState = useMemo(
        () => debounce(async (viewsToSave: ViewState[], widths?: number[]) => {
            if (!challengeId) return
            setIsSaving(true)
            try {
                const tabViews = viewsToContractTabViews(viewsToSave, widths)
                await apis.challengeService.saveWork({
                    challengeId,
                    workState: {
                        tabViews,
                    },
                })
                setLastSaveDate(new Date())
            } catch (err) {
                console.error('Failed to save tabs state:', err)
            } finally {
                setIsSaving(false)
            }
        }, 500),
        [challengeId, setIsSaving, setLastSaveDate]
    )

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            saveWorkspaceTabsState.cancel()
        }
    }, [saveWorkspaceTabsState])

    const handleResetSplit = useCallback(() => {
        panelGroupRef.current?.setLayout([50, 50])
    }, [])

    const sqlTabInitialData = defaultQuery

    /** Move existing tab: remove from source view and open in new view (1 view) or add to other view (2 views). */
    const handleMoveTabToSplitView = useCallback(
        (tab: ChallengeService.WorkspaceTab, sourceViewIndex: number, tabIndexInView: number) => {
            const currentViews = views
            const source = currentViews[sourceViewIndex]
            const newTabsSource = source.tabs.filter((_, i) => i !== tabIndexInView)
            const newActiveSource =
                newTabsSource.length === 0
                    ? 0
                    : Math.min(
                        source.activeTabIndex >= tabIndexInView ? Math.max(0, source.activeTabIndex - 1) : source.activeTabIndex,
                        newTabsSource.length - 1
                    )
            const newViewSource: ViewState = { tabs: newTabsSource, activeTabIndex: newActiveSource }
            const sourceHasNoMoreItems = newTabsSource.length === 0

            if (currentViews.length === 1) {
                if (sourceHasNoMoreItems) {
                    // Only one tab existed; move becomes the single view (no split)
                    const singleView: ViewState = { tabs: [tab], activeTabIndex: 0 }
                    setViews([singleView])
                    setPanelWidths([50])
                    setHasTwoViews(false)
                    viewsRef.current = [singleView]
                    saveWorkspaceTabsState([singleView], [50])
                } else {
                    const newViewWithTab: ViewState = { tabs: [tab], activeTabIndex: 0 }
                    const newViews = [newViewSource, newViewWithTab]
                    const newWidths = [panelWidths[0] ?? 50, 50]
                    setViews(newViews)
                    viewsRef.current = newViews
                    setPanelWidths(newWidths)
                    setHasTwoViews(true)
                    saveWorkspaceTabsState(newViews, newWidths)
                }
                return
            }

            const otherIndex = 1 - sourceViewIndex
            const otherView = currentViews[otherIndex]
            const newTabsOther: ChallengeService.WorkspaceTab[] = [...otherView.tabs, tab]
            const newViewOther: ViewState = { tabs: newTabsOther, activeTabIndex: newTabsOther.length - 1 }

            if (sourceHasNoMoreItems) {
                // Close the now-empty source view; keep only the other view
                const singleView = newViewOther
                setViews([singleView])
                viewsRef.current = [singleView]
                setPanelWidths([50])
                setHasTwoViews(false)
                saveWorkspaceTabsState([singleView], [50])
            } else {
                const newViews = [...currentViews]
                newViews[sourceViewIndex] = newViewSource
                newViews[otherIndex] = newViewOther
                setViews(newViews)
                viewsRef.current = newViews
                saveWorkspaceTabsState(newViews, panelWidths)
            }
        },
        [views, saveWorkspaceTabsState, panelWidths, setHasTwoViews]
    )

    /** Add a new tab in a second view (only when 1 view; used from + menu). */
    const handleAddTabInSplitView = useCallback(
        (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
            if (views.length >= MAX_VIEWS) return
            const newView: ViewState = {
                tabs: [
                    {
                        type: tabType,
                        data: tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? sqlTabInitialData : undefined,
                    },
                ],
                activeTabIndex: 0,
            }
            const newViews: ViewState[] = [...views, newView]
            const newWidths = [...panelWidths, 50]
            setViews(newViews)
            viewsRef.current = newViews
            setPanelWidths(newWidths)
            setHasTwoViews(true)
            saveWorkspaceTabsState(newViews, newWidths)
        },
        [views, saveWorkspaceTabsState, panelWidths, setHasTwoViews, sqlTabInitialData]
    )

    /** Add a new tab in the other view (when 2 views; used from + menu). */
    const handleAddTabInOtherView = useCallback(
        (sourceViewIndex: number) => (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
            if (views.length !== 2) return
            const otherIndex = 1 - sourceViewIndex
            const otherView = views[otherIndex]
            const newTab: ViewTabState = {
                type: tabType,
                data: tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? sqlTabInitialData : undefined,
            }
            const newTabsOther = [...otherView.tabs, newTab]
            const newViewOther: ViewState = { tabs: newTabsOther, activeTabIndex: newTabsOther.length - 1 }
            const newViews = [...views]
            newViews[otherIndex] = newViewOther
            setViews(newViews)
            viewsRef.current = newViews
            saveWorkspaceTabsState(newViews, panelWidths)
        },
        [views, saveWorkspaceTabsState, panelWidths, sqlTabInitialData]
    )

    const handleCloseSecondView = useCallback(() => {
        trackWorkspace('workspace:split:close', { challengeId })
        const newViews: ViewState[] = [views[0]]
        setPanelWidths([50])
        setViews(newViews)
        viewsRef.current = newViews
        setHasTwoViews(false)
        saveWorkspaceTabsState(newViews, [50])
    }, [views, saveWorkspaceTabsState, setHasTwoViews, challengeId])

    /** Structural change: tabs reordered, added, removed, or active tab switched. Updates state and persists. */
    const handleTabsChange = useCallback(
        (viewIndex: number) =>
            (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => {
                setViews((currentViews) => {
                    const next = [...currentViews]
                    if (viewIndex < next.length) {
                        next[viewIndex] = { tabs, activeTabIndex }
                    }
                    viewsRef.current = next
                    saveWorkspaceTabsState(next, panelWidths)
                    return next
                })
            },
        [saveWorkspaceTabsState, panelWidths]
    )

    /** Content-only change: tab data updated (e.g. SQL typing). Updates ref and persists, no rerender. */
    const handleTabsDataChange = useCallback(
        (viewIndex: number) =>
            (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => {
                const current = viewsRef.current
                if (viewIndex >= current.length) return
                const next = [...current]
                next[viewIndex] = { tabs, activeTabIndex }
                viewsRef.current = next
                saveWorkspaceTabsState(next, panelWidths)
            },
        [saveWorkspaceTabsState, panelWidths]
    )

    const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw', 10)

    const resizeTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const resizeInitialRef = useRef(true)

    const handleLayoutChange = useCallback(
        (sizes: number[]) => {
            setPanelWidths(sizes)
            saveWorkspaceTabsState(viewsRef.current, sizes)
            emitChartsTriggerRedraw({})
            if (resizeInitialRef.current) {
                resizeInitialRef.current = false
                return
            }
            if (resizeTrackTimeoutRef.current) clearTimeout(resizeTrackTimeoutRef.current)
            resizeTrackTimeoutRef.current = setTimeout(() => {
                trackWorkspace('workspace:split:resize', { challengeId, sizes })
            }, 1000)
        },
        [saveWorkspaceTabsState, emitChartsTriggerRedraw, challengeId]
    )

    const handleViewDragActiveChange = useCallback(
        (viewIndex: number) => (active: boolean, tabType?: ChallengeService.WorkspaceTabTypeEnum) => {
            setDraggingFromView(active ? viewIndex : null)
            setDraggingTabType(active && tabType ? tabType : null)
        },
        []
    )

    const handleViewDragOutside = useCallback(
        (viewIndex: number) =>
            (tab: ChallengeService.WorkspaceTab, tabIndex: number, pointer: { x: number; y: number }) => {
                if (views.length !== 2) return
                const otherIndex = 1 - viewIndex
                const otherEl = viewContainerRefs.current[otherIndex]
                if (!otherEl) return
                const rect = otherEl.getBoundingClientRect()
                if (pointer.x >= rect.left && pointer.x <= rect.right && pointer.y >= rect.top && pointer.y <= rect.bottom) {
                    handleMoveTabToSplitView(tab, viewIndex, tabIndex)
                }
            },
        [views.length, handleMoveTabToSplitView]
    )

    if (views.length === 1) {
        return (
            <>
                <div className={cn('w-full min-h-0 flex-1 flex', className)}>
                    <WorkspaceTabsView
                        key={viewKeys[0]}
                        ref={undefined}
                        initialTabsState={views[0]}
                        defaultQuery={defaultQuery}
                        onMoveTabToSplitView={(tab, tabIndex) => handleMoveTabToSplitView(tab, 0, tabIndex)}

                        onAddTabInSplitView={handleAddTabInSplitView}
                        addTabInSplitViewTooltipKey="common.openInSplitView"
                        onCloseView={undefined}
                        onTabsChange={handleTabsChange(0)}
                        onTabsDataChange={handleTabsDataChange(0)}
                        openTabTypes={openTabTypes}
                        onPortalTargetChange={handlePortalTargetChange}
                    />
                </div>
                {pythonAllowed && pythonEverMounted && createPortal(
                    <Suspense fallback={
                        <div className="flex items-center justify-center h-full">
                            <svg viewBox="0 0 24 24" fill="#3776AB" className="h-12 w-12 animate-pulse" aria-hidden="true" dangerouslySetInnerHTML={{ __html: '<path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.34.11-.31.17-.28.25-.26.31-.23.38-.2.44-.18.51-.15.58-.12.64-.1.71-.06.77-.04.84-.02 1.27.05zm-6.3 1.98l-.23.33-.08.41.08.41.23.34.33.22.41.09.41-.09.33-.22.23-.34.08-.41-.08-.41-.23-.33-.33-.22-.41-.09-.41.09zm13.09 3.95l.28.06.32.12.35.18.36.27.36.35.35.47.32.59.28.73.21.88.14 1.04.05 1.23-.06 1.23-.16 1.04-.24.86-.32.71-.36.57-.4.45-.42.33-.42.24-.4.16-.36.09-.32.05-.24.02-.16-.01h-8.22v.82h5.84l.01 2.76.02.36-.05.34-.11.31-.17.29-.25.25-.31.24-.38.2-.44.17-.51.15-.58.13-.64.09-.71.07-.77.04-.84.01-1.27-.04-1.07-.14-.9-.2-.73-.25-.59-.3-.45-.33-.34-.34-.25-.34-.16-.33-.1-.3-.04-.25-.02-.2.01-.13v-5.34l.05-.64.13-.54.21-.46.26-.38.3-.32.33-.24.35-.2.35-.14.33-.1.3-.06.26-.04.21-.02.13-.01h5.84l.69-.05.59-.14.5-.21.41-.28.33-.32.27-.35.2-.36.15-.36.1-.35.07-.32.04-.28.02-.21V6.07h2.09l.14.01zm-6.47 14.25l-.23.33-.08.41.08.41.23.33.33.23.41.08.41-.08.33-.23.23-.33.08-.41-.08-.41-.23-.33-.33-.23-.41-.08-.41.08z"/>' }} />
                        </div>
                    }>
                        <LazyPythonNotebook />
                    </Suspense>,
                    pythonHostRef.current!
                )}
            </>
        )
    }

    return (
      <>
        <ResizablePanelGroup
          ref={panelGroupRef}
          direction="horizontal"
          className={cn('w-full h-full', className)}
          onLayout={handleLayoutChange}
        >
          <ResizablePanel
            key={0}
            defaultSize={panelWidths[0] || 50}
            minSize={20}
            maxSize={80}
            order={0}
            className="min-w-0 min-h-0 flex flex-col "
          >
            <div
              ref={(el) => {
                viewContainerRefs.current[0] = el
              }}
              className="h-full min-h-0 flex flex-col"
            >
              <WorkspaceTabsView
                key={viewKeys[0]}
                ref={undefined}
                initialTabsState={views[0]}
                defaultQuery={defaultQuery}
                onMoveTabToSplitView={(tab, tabIndex) =>
                  handleMoveTabToSplitView(tab, 0, tabIndex)
                }
                onAddTabInSplitView={handleAddTabInOtherView(0)}
                addTabInSplitViewTooltipKey="common.openInOtherView"
                onCloseView={undefined}
                onTabsChange={handleTabsChange(0)}
                onTabsDataChange={handleTabsDataChange(0)}
                isSplitView
                siblingIsDragging={draggingFromView === 1}
                siblingDraggedTabType={
                  draggingFromView === 1
                    ? draggingTabType ?? undefined
                    : undefined
                }
                onDragActiveChange={handleViewDragActiveChange(0)}
                onDragOutside={handleViewDragOutside(0)}
                openTabTypes={openTabTypes}
                onPortalTargetChange={handlePortalTargetChange}
              />
            </div>
          </ResizablePanel>
          <ResizableHandle
            className="shrink-0 cursor-col-resize !bg-transparent data-[resize-handle-state=hover]:!bg-transparent data-[resize-handle-state=drag]:!bg-transparent"
            onClick={handleResetSplit}
          >
            <div className={'flex flex-col h-full'}>
              <div
                className={
                  'w-px h-[44px] bg-gradient-to-b from-background to-muted/10'
                }
              />
              <div className={'flex-1 w-px bg-border h-full min-h-0 [[data-resize-handle-state=hover]_&]:bg-primary [[data-resize-handle-state=drag]_&]:bg-primary transition-colors'} />
            </div>
          </ResizableHandle>
          <ResizablePanel
            key={1}
            defaultSize={panelWidths[1] || 50}
            minSize={20}
            maxSize={80}
            order={1}
            className="min-w-0 min-h-0 flex flex-col"
          >
            <div
              ref={(el) => {
                viewContainerRefs.current[1] = el
              }}
              className="h-full min-h-0 flex flex-col"
            >
              <WorkspaceTabsView
                key={viewKeys[1]}
                ref={secondViewRef}
                initialTabsState={views[1]}
                defaultQuery={defaultQuery}
                onMoveTabToSplitView={(tab, tabIndex) =>
                  handleMoveTabToSplitView(tab, 1, tabIndex)
                }
                onAddTabInSplitView={handleAddTabInOtherView(1)}
                addTabInSplitViewTooltipKey="common.openInOtherView"
                onCloseView={handleCloseSecondView}
                onTabsChange={handleTabsChange(1)}
                onTabsDataChange={handleTabsDataChange(1)}
                isSplitView
                siblingIsDragging={draggingFromView === 0}
                siblingDraggedTabType={
                  draggingFromView === 0
                    ? draggingTabType ?? undefined
                    : undefined
                }
                onDragActiveChange={handleViewDragActiveChange(1)}
                onDragOutside={handleViewDragOutside(1)}
                openTabTypes={openTabTypes}
                onPortalTargetChange={handlePortalTargetChange}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
        {pythonAllowed && pythonEverMounted && createPortal(
          <Suspense fallback={null}>
            <LazyPythonNotebook />
          </Suspense>,
          pythonHostRef.current!
        )}
      </>
    )
}

